/* iceberg-ui */
import { Card, Flex } from '@auviknetworks/iceberg-ui';

/* MUI */
import { CircularProgress } from '@mui/material';
import { EmptyState } from 'application/Shared/Components/Dashboard/Panels/EmptyPanel';
import { Route } from 'application/Shared/Helpers';

/* React */
import React from 'react';
import { useParams } from 'react-router';

/* Components */
import { getWorkatoStatus } from '../functions';
import { WorkatoStatus } from '../types';
import { useConnectionsAndRecipesContext } from './ConnectionsAndRecipesContext';

/**
 * A component that checks for valid Workato connections and recipes before rendering children
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render if checks pass
 * @returns {JSX.Element} Loading spinner, no integrations message, or children based on connection status
 */

export const ConnectionsAndRecipesChecker = ({
    children
}: {
    children: React.ReactNode;
}) => {
    const { clientId, partnerId } = useParams();

    const { loading, connections, recipesByProvider } =
        useConnectionsAndRecipesContext();

    const hasValidConnectionsWithRecipes = connections?.some((connection) => {
        if (!connection?.provider) {
            return false;
        }
        const recipesForConnection = recipesByProvider[connection.provider];

        return (
            getWorkatoStatus(connection) === WorkatoStatus.connected &&
            !!recipesForConnection?.length
        );
    });

    if (loading) {
        return (
            <Flex justifyContent="center" mt={5}>
                <CircularProgress />
            </Flex>
        );
    }

    const baseAdminRoute = Route.buildBaseAdminRoute({
        clientId: clientId!,
        partnerId
    });

    if (!hasValidConnectionsWithRecipes) {
        return (
            // We set the card to be full screen, subtract the header (60px) and margins (2✕24px) from the height
            <Card sx={{ height: 'calc(100vh - 60px - 48px)', margin: '24px' }}>
                <EmptyState
                    content="SaasOps.emptyState.message"
                    actions={[
                        {
                            href: `${baseAdminRoute}/saasOpsIntegrations`,
                            label: 'SaasOps.emptyState.setUpIntegration'
                        }
                    ]}
                    image="calendar"
                />
            </Card>
        );
    }

    return children;
};
